import { useAuth0 } from '@auth0/auth0-react';
import { Badge, ButtonGroup } from '@serodigital/ui-components';
import * as React from 'react';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { TrialName } from '../../../../../backend/shared/types/trial';
import { BeeStatus } from '../../../../shared/api-clients/_api';
import { isLessThan1HourAgo } from '../../../../shared/helpers/dateHelpers';
import { BackButton } from '../../components/backButton';
import { HeatingSchedulesProvider } from '../../contexts/heatingSchedules';
import { HotWaterSchedulesProvider } from '../../contexts/hotWaterSchedules';
import { routePaths } from '../../routes';
import { trialBadgeClassname } from '../../tables/useTableDefinition';
import { Address } from './address';
import { ConfigView } from './config/view';
import { DataLabel } from './dataLabel';
import { Overview } from './details/overview';
import { Peripheral } from './details/peripheral';
import { Firmware } from './firmware';
import { HeatingView } from './heating/view';
import { HotWaterView } from './hotwater/view';
import './index.css';
import { SettingsView } from './settings/view';
import { useDetails } from './state/detailsContext';
import { equinoxAbbreviations } from './state/enums';
import { IDeviceInfo } from './state/types';
import { Trials } from './trials';

type DataFetcher<T> = {
  label: string;
  key: string;
  getter: (data?: T) => string | number | boolean | JSX.Element;
};

function generateURL(beeId: string) {
  return `/bee/${beeId}/details`;
}

export const equinoxLabels = {
  [TrialName.EQUINOX_GROUP_A]: equinoxAbbreviations.EQ_A,
  [TrialName.EQUINOX_GROUP_B]: equinoxAbbreviations.EQ_B,
};

export function BeeDetailsView() {
  const { deviceInfo, deviceId, controlPeripherals, active, setActiveNav, refetchBeeDetails, trials } = useDetails();

  const { pathname } = useLocation();
  const { replace, push } = useHistory();
  const { path } = useRouteMatch();
  const { getIdTokenClaims } = useAuth0();
  const [roles, setRoles] = React.useState(['']);

  function onNavigateClick(url: string, index: number) {
    const genUrl = generateURL(deviceId) + url;
    if (pathname === genUrl) {
      return;
    }
    setActiveNav(index);
    push(genUrl);
  }

  React.useEffect(() => {
    async function userRoles() {
      const claims = await getIdTokenClaims();

      setRoles(claims?.['https://sero-digital.sero.life/roles']);
    }

    userRoles();
  }, []);

  const isNavDisabled = deviceInfo?.status !== BeeStatus.Online;
  const hasNoData = !isLessThan1HourAgo(deviceInfo?.lastStatusUpdate);

  const rolesAllowedToCreateAndEditTrials = ['HCM L2 Support', 'HCM L3 Support'];
  const allowedToManageTrials = roles?.some((role) => rolesAllowedToCreateAndEditTrials.includes(role));
  const allowedToManageConfig = roles?.some((role) => role === 'HCM L3 Support');
  const allowedToEditTags = roles?.some((role) => role === 'HCM L3 Support');
  const rolesAllowedToViewPeripheralData = ['HCM L2 Support', 'HCM L3 Support'];
  const canViewPeripheralData = roles?.some((role) => rolesAllowedToViewPeripheralData.includes(role));

  const dataLabelList = React.useMemo(() => {
    const dataLabels: DataFetcher<IDeviceInfo>[] = [
      { label: 'UPRN', key: 'uprn', getter: (data: IDeviceInfo) => data.uprn },
      {
        label: 'UDPRN',
        key: 'udprn',
        getter: (data: IDeviceInfo) => data.udprn,
      },
      {
        label: 'Project',
        key: 'project',
        getter: (data: IDeviceInfo) => data.project,
      },
      {
        label: 'Plot Number',
        key: 'plot',
        getter: (data: IDeviceInfo) => data.plot,
      },
      {
        label: 'Trials',
        key: 'trials',
        getter: () => {
          if (trials?.length > 0 || allowedToManageTrials) {
            return (
              <div className="flex justify-center gap-24">
                {trials?.map((trial, index) => {
                  return (
                    <>
                      {trial.id === deviceInfo?.bee?.trialId ? (
                        <Badge
                          data-qa={`trials-${trial.name}`}
                          className={trialBadgeClassname(deviceInfo?.bee?.inActiveTrialEvent)[trial.name]}
                          key={`${trial}-${index}`}
                        >
                          {equinoxLabels[trial.name] || trial.name}
                        </Badge>
                      ) : null}
                    </>
                  );
                })}
                {allowedToManageTrials && (
                  <Trials deviceInfo={deviceInfo} trials={trials} refetch={refetchBeeDetails} />
                )}
              </div>
            );
          }
        },
      },
    ];

    return dataLabels;
  }, [deviceInfo, trials]);

  return (
    <div data-qa="bee-details" className="flex flex-col w-full h-full px-72">
      <div className="mt-16 w-84">
        <BackButton onClick={() => replace(routePaths.home.root)} />
      </div>
      <div className="flex justify-between">
        <Address {...deviceInfo?.physicalAddress} />
        <Firmware deviceId={deviceId} refetch={refetchBeeDetails} />
      </div>
      <div className="flex space-x-56 py-16">
        {dataLabelList.map(({ label, key, getter }) => (
          <DataLabel key={key} label={label} value={getter(deviceInfo)} />
        ))}
      </div>
      <div className="flex justify-center mb-32">
        <ButtonGroup
          data-qa="navigation"
          active={active}
          buttons={[
            {
              label: 'BEE Overview',
              onClick: (index) => onNavigateClick('/', index),
            },
            {
              label: 'Hot Water',
              onClick: (index) => onNavigateClick('/hotwater', index),
              disabled: isNavDisabled,
            },
            {
              label: 'Heating',
              onClick: (index) => onNavigateClick('/heating', index),
              disabled: isNavDisabled,
            },
            {
              label: 'Settings',
              onClick: (index) => onNavigateClick('/settings', index),
              disabled: isNavDisabled,
            },
            {
              label: 'Configuration',
              onClick: (index) => onNavigateClick('/configuration', index),
              disabled: isNavDisabled || !allowedToManageConfig,
            },
          ]}
        />
      </div>
      <Switch>
        <Route exact path={path}>
          <div className="flex flex-row space-x-8 pt-8">
            <Overview {...deviceInfo} />
            <Peripheral
              onlineStatus={deviceInfo.status}
              peripherals={deviceInfo.peripheralHealthChecks}
              canViewPeripheralData={canViewPeripheralData}
            />
          </div>
        </Route>
        <Route path={`${path}/hotwater`}>
          <HotWaterSchedulesProvider deviceId={deviceId}>
            <HotWaterView
              temperatureData={deviceInfo?.config?.device_config?.hot_water_temperature}
              deviceId={deviceId}
              hasNoData={hasNoData}
            />
          </HotWaterSchedulesProvider>
        </Route>
        <Route path={`${path}/heating`}>
          <HeatingSchedulesProvider deviceId={deviceId}>
            <HeatingView hasNoData={hasNoData} />
          </HeatingSchedulesProvider>
        </Route>
        <Route path={`${path}/settings`}>
          <SettingsView peripherals={controlPeripherals} bee={deviceInfo.bee} allowedToEditTags={allowedToEditTags} />
        </Route>
        <Route path={`${path}/configuration`}>
          <ConfigView deviceId={deviceId} />
        </Route>
      </Switch>
    </div>
  );
}
